import { makeGetRequest, makePutRequest } from 'lib/axios';
import { BagelChatMessage, BagelConversationSummary } from '../types';

/** sessionStorage key for the active Rosie Bagel chat workflow id */
export const BAGEL_WORKFLOW_ID_STORAGE_KEY = 'bagelWorkflowId';

interface WorkflowSessionPayload {
  messages: Pick<BagelChatMessage, 'role' | 'content'>[];
  pageContext: string | null;
}

/**
 * List recent Bagel chat sessions for the current user.
 */
export async function listWorkflowSessions(): Promise<
  BagelConversationSummary[]
> {
  try {
    const res = await makeGetRequest<{
      message: string;
      payload: BagelConversationSummary[];
    }>('/bagel/chat/sessions', {
      suppress403Log: true,
      suppress404Log: true,
    });
    return res.data?.payload ?? [];
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    if (status === 403 || status === 404) return [];
    throw error;
  }
}

/**
 * Restore a persisted conversation session by workflowId.
 * Returns null if the session doesn't exist (404) — treat as a fresh conversation.
 */
export async function getWorkflowSession(
  workflowId: string,
): Promise<WorkflowSessionPayload | null> {
  try {
    const res = await makeGetRequest<{
      message: string;
      payload: WorkflowSessionPayload;
    }>(`/bagel/chat/session/${workflowId}`, {
      suppress403Log: true,
      suppress404Log: true,
    });
    return res.data?.payload ?? null;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    // 403/404 = no access yet, session expired, or never existed
    if (status === 403 || status === 404) return null;
    throw error;
  }
}

/**
 * Upsert (save) the current conversation state after each completed turn.
 * Fire-and-forget — do not block the UI on this.
 */
export async function saveWorkflowSession(
  workflowId: string,
  messages: BagelChatMessage[],
  pageContext?: string,
): Promise<void> {
  await makePutRequest(`/bagel/chat/session/${workflowId}`, {
    messages: messages
      .filter(m => m.content.trim())
      .map(({ role, content }) => ({ role, content })),
    pageContext: pageContext ?? null,
  });
}
